import _ from "lodash";
import { v4 as uuidv4 } from "uuid";

/**
 * Adds tools for table axis calibration.
 */
function apply(wpd, wpdWindow, wpdDocument, dataEntryVM, ids) {
	const hitTolerance = 8;

	/**
	 * Alignment tool for drawing a rectangle around tabular data.
	 * @return {Function} - WPD Tool
	 */
	wpd.AlignmentAreaTool = (function () {
		const Tool = function (calibration) {
			let isMouseDown = false;
			let isSelecting = false;
			let _drawTimer = null;

			const nextButton = wpdDocument
				.getElementById(ids.areaSidebar)
				.querySelector(".table-area-calibration-next-button");
			const rotationButtons = wpdDocument.querySelectorAll(
				"#topContainer button[title^='Rotate']",
			);

			this.onAttach = function () {
				wpd.graphicsWidget.resetData();

				// if calibration data passed in, load them
				if (calibration.area.length) {
					// enable complete button
					nextButton.disabled = false;
				}

				// paint the loaded data
				wpd.graphicsWidget.setRepainter(
					new wpd.TableRepainter({
						area: calibration.area,
						rotation: calibration.rotation,
						drawHeaders: false,
					}),
				);
				wpd.graphicsWidget.forceHandlerRepaint();

				// disable rotation buttons
				rotationButtons.forEach((el) => (el.disabled = true));
			};

			this.onRemove = function () {
				wpd.graphicsWidget.resetData();

				// disable rotation buttons
				rotationButtons.forEach((el) => (el.disabled = false));
			};

			this.onMouseDown = function () {
				isMouseDown = true;
			};

			this.onMouseUp = function (e) {
				if (isSelecting === true) {
					// enable complete button
					nextButton.disabled = false;

					// clear the draw timer
					clearTimeout(_drawTimer);

					wpd.graphicsWidget.updateZoomOnEvent(e);

					// push these reset statements to the bottom of the events message queue
					isSelecting = false;
					isMouseDown = false;
				} else {
					isMouseDown = false;
				}
			};

			this.onMouseMove = function (e, pos, imagePos) {
				if (isMouseDown === true) {
					if (!isSelecting) {
						// new selection
						isSelecting = true;

						// disable complete button
						nextButton.disabled = true;

						// clear previous second rectangle point
						calibration.area[1] = null;

						// record the first rectangle point
						calibration.area[0] = imagePos;
					}

					// record the new position as the second selection rectangle point
					calibration.area[1] = imagePos;

					// refresh the rectangle every 1 ms
					clearTimeout(_drawTimer);
					_drawTimer = setTimeout(
						function () {
							wpd.graphicsWidget.forceHandlerRepaint();
						}.bind(this),
						1,
					);
				}
			};
		};

		return Tool;
	})();

	// Resizing table
	wpd.EditAlignmentAreaTool = (function () {
		const Tool = function (calibration) {
			let isMouseDown = false;
			let isSelecting = false;
			let _drawTimer = null;

			const selectedBorder = {
				edge: null,
				corner: null,
				xIndex: null,
				yIndex: null,
			};
			const rotation = calibration.rotation ?? 0;
			const rows = calibration.rows;
			const cols = calibration.cols;
			const area = calibration.area;

			const nextButton = wpdDocument
				.getElementById(ids.areaSidebar)
				.querySelector(".table-area-calibration-next-button");
			const rotationButtons = wpdDocument.querySelectorAll(
				"#topContainer button[title^='Rotate']",
			);

			this.onAttach = function () {
				wpd.graphicsWidget.resetData();

				// if calibration data passed in, load them
				if (calibration.area.length) {
					// enable complete button
					nextButton.disabled = false;
				}

				// paint the loaded data
				wpd.graphicsWidget.setRepainter(
					new wpd.TableRepainter({
						area: calibration.area,
						rows: calibration.rows,
						cols: calibration.cols,
						rotation: calibration.rotation,
						drawHeaders: false,
						selectedBorder: selectedBorder,
					}),
				);
				wpd.graphicsWidget.forceHandlerRepaint();

				// disable rotation buttons
				rotationButtons.forEach((el) => (el.disabled = true));
			};

			this.onRemove = function () {
				wpd.graphicsWidget.resetData();

				// disable rotation buttons
				rotationButtons.forEach((el) => (el.disabled = false));
			};

			this.onMouseDown = function () {
				isMouseDown = true;
			};

			this.onMouseUp = function (e) {
				if (isSelecting === true) {
					// enable complete button
					nextButton.disabled = false;

					// clear the draw timer
					clearTimeout(_drawTimer);

					wpd.graphicsWidget.updateZoomOnEvent(e);

					// push these reset statements to the bottom of the events message queue
					isSelecting = false;
					isMouseDown = false;
				} else {
					isMouseDown = false;
				}
			};

			this.onMouseMove = function (e, pos, imagePos) {
				const { corner, edge, xIndex, yIndex } = selectedBorder;
				const threshold = 60;

				if (isMouseDown === true) {
					const axis = wpd.tree.getActiveAxes();
					const movementRules = wpd.alignAxes.borderMovementRules(
						imagePos,
						area,
						threshold,
					);

					if (corner && movementRules[corner]) {
						if (movementRules[corner].x) area[xIndex].x = imagePos.x;
						if (movementRules[corner].y) area[yIndex].y = imagePos.y;
					} else if (edge && movementRules[edge]) {
						if (movementRules[edge].x) area[xIndex].x = imagePos.x;
						if (movementRules[edge].y) area[yIndex].y = imagePos.y;
					}

					wpd.alignAxes.resetDividers(area, rows, cols, rotation);
					const metadata = axis.getMetadata();
					metadata.calibration = calibration;
					axis.setMetadata(metadata);
				} else {
					Object.assign(selectedBorder, {
						edge: null,
						corner: null,
						xIndex: null,
						yIndex: null,
					});
					const isInTable = wpd.alignAxes.isInTableArea(imagePos, area, 10);

					if (isInTable) {
						const borderResult = wpd.alignAxes.borderHitTest(
							imagePos,
							area,
							rotation,
						);

						if (borderResult.corner || borderResult.edge) {
							Object.assign(selectedBorder, borderResult);
							e.target.style.cursor = "grab";
						} else {
							e.target.style.cursor = "crosshair";
						}
					} else {
						e.target.style.cursor = "crosshair";
					}
				}
				wpd.graphicsWidget.forceHandlerRepaint();
			};
		};

		return Tool;
	})();

	// Removing rows or columns
	wpd.RemoveRowOrColumnTool = (function () {
		const Tool = function (calibration, isRow) {
			const area = calibration.area;
			const rows = calibration.rows;

			const cols = calibration.cols;
			const rotation = calibration.rotation ?? 0;
			const rotationButtons = wpdDocument.querySelectorAll(
				"#topContainer button[title^='Rotate']",
			);

			const allDividers = wpd.alignAxes.getFullDividers(
				area,
				rotation,
				rows.dividers,
				cols.dividers,
			);

			const allRowDividers = allDividers.rows;
			const allColDividers = allDividers.cols;
			const allRows = {
				...rows,
				dividers: allRowDividers,
				num: rows.num + 2,
			};
			const allCols = {
				...cols,
				dividers: allColDividers,
				num: cols.num + 2,
			};

			//TODO: make the naming more clear, same for add row/col
			const selectedIndexes = {
				row: null,
				col: null,
				nextRow: null,
				nextCol: null,
				rowIndex: null,
				colIndex: null,
			};

			const clearSelected = () => {
				Object.assign(selectedIndexes, {
					col: null,
					row: null,
					nextCol: null,
					nextRow: null,
					colIndex: null,
					rowIndex: null,
				});
			};

			this.onAttach = function () {
				wpd.graphicsWidget.resetData();

				// set the repainter
				wpd.graphicsWidget.setRepainter(
					new wpd.TableRepainter({
						area: area,
						rows: allRows,
						cols: allCols,
						rotation: rotation,
						drawHeaders: false,
						selectedDividers: selectedIndexes,
						removePreview: true,
						editing: true,
						isRow: isRow,
					}),
				);

				// paint the loaded data
				wpd.graphicsWidget.forceHandlerRepaint();

				// disable rotation buttons
				rotationButtons.forEach((el) => (el.disabled = true));
			};

			this.onRemove = function () {
				wpd.graphicsWidget.resetData();

				// disable rotation buttons
				rotationButtons.forEach((el) => (el.disabled = false));
			};

			this.onMouseMove = function (e, pos, imagePos) {
				const isInTable = wpd.alignAxes.isInTableArea(imagePos, area);
				if (isInTable) {
					if (isRow && _.isNil(selectedIndexes.col) && rows.num > 1) {
						for (let i = 0; i < allRowDividers.length - 1; i++) {
							const row = wpd.alignAxes.getRow(
								i,
								allRowDividers,
								area,
								imagePos,
								rotation,
							);

							if (row.isHovered) {
								Object.assign(selectedIndexes, {
									col: null,
									nextCol: null,
									colIndex: null,
									row: row.startDividerIndex,
									nextRow: row.endDividerIndex,
									rowIndex: row.rowIndex,
								});

								e.target.style.cursor = "not-allowed";
								break;
							} else {
								clearSelected();
								e.target.style.cursor = "crosshair";
							}
						}
					}

					if (!isRow && _.isNil(selectedIndexes.row) && cols.num > 1) {
						for (let i = 0; i < allColDividers.length - 1; i++) {
							const col = wpd.alignAxes.getCol(
								i,
								allColDividers,
								area,
								imagePos,
								rotation,
							);

							if (col.isHovered) {
								Object.assign(selectedIndexes, {
									col: col.startDividerIndex,
									nextCol: col.endDividerIndex,
									colIndex: col.colIndex,
									row: null,
									nextRow: null,
									rowIndex: null,
								});

								e.target.style.cursor = "not-allowed";
								break;
							} else {
								clearSelected();
								e.target.style.cursor = "crosshair";
							}
						}
					}
				} else {
					clearSelected();

					e.target.style.cursor = "crosshair";
				}

				wpd.graphicsWidget.forceHandlerRepaint();
			};

			this.onMouseDown = function (e, pos, imagePos) {
				const axis = wpd.tree.getActiveAxes();
				const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);

				if (isRow && !_.isNil(selectedIndexes.row) && rows.num > 1) {
					const delInd = Math.max(0, selectedIndexes.row);
					rows.dividers.splice(Math.max(delInd - 1, 0), 1);
					rows.headers.splice(delInd, 1);
					allRows.dividers.splice(Math.max(delInd - 1, 0) + 1, 1);
					rows.num -= 1;
					allRows.num -= 1;

					const rowIndex = selectedIndexes.rowIndex;
					datasets.forEach((ds) => {
						const pixels = ds.getAllPixels();
						const currentRowPixelIndex = pixels.findIndex(
							(px) => px.metadata?.rowIndex === rowIndex,
						);

						// Remove pixel that belonged to the deleted row
						if (currentRowPixelIndex !== -1) {
							ds.removePixelAtIndex(currentRowPixelIndex);
						}

						// Decrement the rowIndex of the pixels in subsequent rows
						pixels.forEach((px) => {
							const pxRowIndex = px.metadata.rowIndex;

							if (pxRowIndex > selectedIndexes.rowIndex) {
								px.metadata.rowIndex -= 1;
							}
						});
					});

					const metadata = axis.getMetadata();
					metadata.calibration = calibration;
					axis.setMetadata(metadata);
				} else if (!isRow && !_.isNil(selectedIndexes.col) && cols.num > 1) {
					const delInd = Math.max(0, selectedIndexes.col);
					cols.dividers.splice(Math.max(delInd - 1, 0), 1);
					cols.headers.splice(delInd, 1);
					allCols.dividers.splice(Math.max(delInd - 1, 0) + 1, 1);
					cols.num -= 1;
					allCols.num -= 1;

					const colIndex = selectedIndexes.colIndex;
					const colDataset = datasets[colIndex];
					if (colDataset) {
						for (let i = colIndex + 1; i < datasets.length; i++) {
							const ds = datasets[i];
							const md = ds.getMetadata();
							md.colIndex -= 1;
							ds.setMetadata(md);
						}

						// delete the dataset associated with this row
						wpd.appData.getPlotData().deleteDataset(colDataset);
					}

					const metadata = axis.getMetadata();
					metadata.calibration = calibration;
					axis.setMetadata(metadata);
				}

				wpd.graphicsWidget.removeTool();
				wpd.graphicsWidget.setTool(
					new wpd.AlignmentRowsAndColumnsTool(calibration),
				);
				wpd.graphicsWidget.forceHandlerRepaint();
			};
		};

		return Tool;
	})();

	// Adding rows or columns
	wpd.AddRowOrColumnDividerTool = (function () {
		const Tool = function (calibration, isRow) {
			const area = calibration.area;
			const rows = calibration.rows;
			const cols = calibration.cols;
			const rotation = calibration.rotation ?? 0;

			const allDividers = wpd.alignAxes.getFullDividers(
				area,
				rotation,
				rows.dividers,
				cols.dividers,
			);

			const allRowDividers = allDividers.rows;
			const allColDividers = allDividers.cols;

			const allRows = {
				...rows,
				dividers: allRowDividers,
				num: rows.num + 2,
			};
			const allCols = {
				...cols,
				dividers: allColDividers,
				num: cols.num + 2,
			};

			const selectedIndexes = {
				row: null,
				col: null,
				nextRow: null,
				nextCol: null,
				previewRow: null,
				previewCol: null,
				rowIndex: null,
				colIndex: null,
			};

			const rotationButtons = wpdDocument.querySelectorAll(
				"#topContainer button[title^='Rotate']",
			);
			this.onAttach = function () {
				wpd.graphicsWidget.resetData();

				// set the repainter
				wpd.graphicsWidget.setRepainter(
					new wpd.TableRepainter({
						area: area,
						rows: allRows,
						cols: allCols,
						rotation: rotation,
						drawHeaders: false,
						selectedDividers: selectedIndexes,
						addPreview: true,
						isRow: isRow,
						editing: true,
					}),
				);

				// paint the loaded data
				wpd.graphicsWidget.forceHandlerRepaint();

				// disable rotation buttons
				rotationButtons.forEach((el) => (el.disabled = true));
			};

			this.onRemove = function () {
				wpd.graphicsWidget.resetData();

				// disable rotation buttons
				rotationButtons.forEach((el) => (el.disabled = false));
			};

			this.onMouseDown = function (e, pos, imagePos) {
				const axis = wpd.tree.getActiveAxes();
				const plotData = wpd.appData.getPlotData();
				const datasets = plotData.getDatasetsForAxis(axis);
				const fileManager = wpd.appData.getFileManager();
				const pageManager = wpd.appData.getPageManager();
				if (isRow && !_.isNil(selectedIndexes.row)) {
					const dividerInd = Math.min(
						selectedIndexes.row,
						selectedIndexes.nextRow,
					);

					rows.dividers.splice(dividerInd, 0, selectedIndexes.previewRow);
					rows.headers.splice(selectedIndexes.row, 0, null);
					allRows.dividers.splice(
						dividerInd + 1,
						0,
						selectedIndexes.previewRow,
					);

					rows.num += 1;
					allRows.num += 1;

					datasets.forEach((ds) => {
						const pixels = ds.getAllPixels();
						pixels.forEach((px, i) => {
							const rowIndex = px.metadata.rowIndex;
							if (rowIndex >= selectedIndexes.rowIndex) {
								px.metadata.rowIndex += 1;
								ds.setMetadataAt(i, px.metadata);
							}
						});
					});
					const metadata = axis.getMetadata();
					metadata.calibration = calibration;
					axis.setMetadata(metadata);
				} else if (!isRow && !_.isNil(selectedIndexes.col)) {
					const dividerInd = Math.min(
						selectedIndexes.col,
						selectedIndexes.nextCol,
					);

					cols.dividers.splice(dividerInd, 0, selectedIndexes.previewCol);
					cols.headers.splice(selectedIndexes.col, 0, null);
					allCols.dividers.splice(
						dividerInd + 1,
						0,
						selectedIndexes.previewCol,
					);

					cols.num += 1;
					allCols.num += 1;

					// first, delete datasets after the index of the newly added dataset
					const colIndex = selectedIndexes.colIndex;
					const deleteDatasets = [];
					for (let i = colIndex; i < datasets.length; i++) {
						const md = datasets[i].getMetadata();
						md.colIndex += 1;
						datasets[i].setMetadata(md);
						deleteDatasets.push(datasets[i]);
						plotData.deleteDataset(datasets[i]);
					}

					// add the new dataset at the right index
					wpd.alignAxes.addDataset(colIndex, new wpd.ImageAxes());

					// re-add the deleted datasets
					deleteDatasets.forEach((ds) => {
						plotData.addDataset(ds);
						fileManager.addDatasetsToCurrentFile([ds]);
						if (wpd.appData.isMultipage()) {
							pageManager.addDatasetsToCurrentPage([ds]);
						}
						wpd.events.dispatch("wpd.dataset.add", {
							dataset: ds,
						});
					});
					const metadata = axis.getMetadata();
					metadata.calibration = calibration;
					axis.setMetadata(metadata);
				}

				wpd.graphicsWidget.removeTool();
				wpd.graphicsWidget.setTool(
					new wpd.AlignmentRowsAndColumnsTool(calibration),
				);
				wpd.graphicsWidget.forceHandlerRepaint();
			};

			this.onMouseMove = function (e, pos, imagePos) {
				const isInTable = wpd.alignAxes.isInTableArea(imagePos, area);
				if (isInTable) {
					const rowPos = wpd.alignAxes.getRowPos(imagePos, rotation);
					const colPos = wpd.alignAxes.getColPos(imagePos, rotation);
					if (isRow && selectedIndexes.col == null) {
						for (let i = 0; i < allRowDividers.length; i++) {
							const row = wpd.alignAxes.getRow(
								i,
								allRowDividers,
								area,
								imagePos,
								rotation,
							);

							if (row.isPreviewHovered) {
								selectedIndexes.row = row.startDividerIndex;
								selectedIndexes.nextRow = row.previewIndex;
								selectedIndexes.previewRow = rowPos;
								selectedIndexes.rowIndex = row.rowIndex;

								e.target.style.cursor = "copy";
								break;
							} else {
								selectedIndexes.row = null;
								selectedIndexes.nextRow = null;
								selectedIndexes.previewRow = null;
								selectedIndexes.rowIndex = null;

								e.target.style.cursor = "crosshair";
							}
						}
					}

					if (!isRow && selectedIndexes.row == null) {
						for (let i = 0; i < allColDividers.length; i++) {
							const col = wpd.alignAxes.getCol(
								i,
								allColDividers,
								area,
								imagePos,
								rotation,
							);

							if (col.isPreviewHovered) {
								selectedIndexes.col = col.startDividerIndex;
								selectedIndexes.nextCol = col.previewIndex;
								selectedIndexes.previewCol = colPos;
								selectedIndexes.colIndex = col.colIndex;

								e.target.style.cursor = "copy";
								break;
							} else {
								selectedIndexes.col = null;
								selectedIndexes.nextCol = null;
								selectedIndexes.previewCol = null;
								selectedIndexes.colIndex = null;

								e.target.style.cursor = "crosshair";
							}
						}
					}
				} else {
					Object.assign(selectedIndexes, {
						col: null,
						row: null,
						nextCol: null,
						nextRow: null,
						previewCol: null,
						previewRow: null,
						rowIndex: null,
						colIndex: null,
					});

					e.target.style.cursor = "crosshair";
				}

				wpd.graphicsWidget.forceHandlerRepaint();
			};
		};

		return Tool;
	})();

	/**
	 * Alignment tool for defining rows and columns of tabular data.
	 * @return {Function} WPD Tool
	 */
	wpd.AlignmentRowsAndColumnsTool = (function () {
		const Tool = function (calibration) {
			const area = calibration.area;
			const rows = calibration.rows;
			const cols = calibration.cols;
			const rotation = calibration.rotation ?? 0;

			// html elements to manipulate
			const completeButton = wpdDocument
				.getElementById(ids.rcSidebar)
				.querySelector(".table-rows-columns-calibration-next-button");
			const rotationButtons = wpdDocument.querySelectorAll(
				"#topContainer button[title^='Rotate']",
			);

			const selectedIndexes = {
				row: null,
				col: null,
			};
			let isMouseDown = false;

			const moveLine = (i, imageP, imageDividers) => {
				if (i !== null) {
					imageDividers[i] = imageP;
					wpd.graphicsWidget.forceHandlerRepaint();
				}
			};

			const resizeRest = (i, imageP, imageDividers, min, max) => {
				const isAfterNextDivider =
					min < max
						? imageP > imageDividers[i + 1]
						: imageP < imageDividers[i + 1];
				const isBeforePreviousDivider =
					min < max
						? imageP < imageDividers[i - 1]
						: imageP > imageDividers[i - 1];

				// resize overlapped divisions, except first and last ones
				if (i < imageDividers.length && isAfterNextDivider) {
					// calculate the remaining space
					const delta = (max - imageP) / (imageDividers.length - i);

					// divide equally among the rest
					for (let j = i + 1; j < imageDividers.length; j++) {
						imageDividers[j] = imageP + delta * (j - i);
					}

					// repaint
					wpd.graphicsWidget.forceHandlerRepaint();
				} else if (i > 0 && isBeforePreviousDivider) {
					// calculate the remaining space
					const delta = (imageP - min) / (i + 1);

					// divide equally among the rest
					for (let j = 0; j < i; j++) {
						imageDividers[j] = min + delta * (j + 1);
					}

					// repaint
					wpd.graphicsWidget.forceHandlerRepaint();
				}
			};

			this.onAttach = function () {
				wpd.graphicsWidget.resetData();

				// set the repainter
				wpd.graphicsWidget.setRepainter(
					new wpd.TableRepainter({
						area: area,
						rows: rows,
						cols: cols,
						rotation: rotation,
						drawHeaders: false,
						selectedDividers: selectedIndexes,
					}),
				);

				// paint the loaded data
				wpd.graphicsWidget.forceHandlerRepaint();

				// enable complete button
				completeButton.disabled = false;

				// disable rotation buttons
				rotationButtons.forEach((el) => (el.disabled = true));

				// listen for divider updates
				wpd.events.removeAllListeners("wpd.tableAxis.updateDividers");
				wpd.events.addListener("wpd.tableAxis.updateDividers", () =>
					wpd.alignAxes.resetDividers(area, rows, cols, rotation),
				);
			};

			this.onRemove = function () {
				wpd.graphicsWidget.resetData();

				// enable rotation buttons
				rotationButtons.forEach((el) => (el.disabled = false));
			};

			this.onMouseDown = function (e, pos, imagePos) {
				isMouseDown = true;
				const isInTable = wpd.alignAxes.isInTableArea(imagePos, area);

				if (isInTable) {
					const rowPos = wpd.alignAxes.getRowPos(imagePos, rotation);
					const colPos = wpd.alignAxes.getColPos(imagePos, rotation);

					if (selectedIndexes.col == null) {
						for (let i = 0; i < rows.dividers.length; i++) {
							const rowHit = wpd.alignAxes.dividerHitTest(
								rowPos,
								rows.dividers[i],
								16,
							);
							if (rowHit) {
								selectedIndexes.row = i;
								e.target.style.cursor = "grabbing";
								break;
							}
						}
					}

					if (selectedIndexes.row == null) {
						for (let i = 0; i < cols.dividers.length; i++) {
							const colHit = wpd.alignAxes.dividerHitTest(
								colPos,
								cols.dividers[i],
								16,
							);
							if (colHit) {
								selectedIndexes.col = i;
								e.target.style.cursor = "grabbing";
								break;
							}
						}
					}
				}
			};

			this.onMouseMove = function (e, pos, imagePos) {
				const isInTable = wpd.alignAxes.isInTableArea(imagePos, area);

				// test for table area
				if (isInTable) {
					const rowPos = wpd.alignAxes.getRowPos(imagePos, rotation);
					const colPos = wpd.alignAxes.getColPos(imagePos, rotation);
					if (isMouseDown) {
						const isInArea = wpd.alignAxes.isInTableArea(imagePos, area);

						if (isInArea) {
							// move the row division line
							moveLine(selectedIndexes.row, rowPos, rows.dividers);

							// move the column division line
							moveLine(selectedIndexes.col, colPos, cols.dividers);
						}
					} else {
						// update cursor and highlight hovered line if necessary
						if (_.isNil(selectedIndexes.col)) {
							for (let i = 0; i < rows.dividers.length; i++) {
								const rowHit = wpd.alignAxes.dividerHitTest(
									rowPos,
									rows.dividers[i],
									16,
								);
								if (rowHit) {
									selectedIndexes.row = i;
									e.target.style.cursor = "grab";
									break;
								} else {
									selectedIndexes.row = null;
									e.target.style.cursor = "crosshair";
								}
							}
						}

						if (_.isNil(selectedIndexes.row)) {
							for (let i = 0; i < cols.dividers.length; i++) {
								const colHit = wpd.alignAxes.dividerHitTest(
									colPos,
									cols.dividers[i],
									16,
								);

								if (colHit) {
									selectedIndexes.col = i;
									e.target.style.cursor = "grab";
									break;
								} else {
									selectedIndexes.col = null;
									e.target.style.cursor = "crosshair";
								}
							}
						}

						// repaint
						wpd.graphicsWidget.forceHandlerRepaint();
					}
				} else {
					e.target.style.cursor = "crosshair";
					if (!isMouseDown) {
						selectedIndexes.row = null;
						selectedIndexes.col = null;
						wpd.graphicsWidget.forceHandlerRepaint();
					}
				}
			};

			this.onMouseUp = function (e, pos, imagePos) {
				const edges = wpd.alignAxes.getEdges(area, rotation);

				// resize the overlapped row and column divisions
				if (!_.isNil(selectedIndexes.row)) {
					resizeRest(
						selectedIndexes.row,
						rows.dividers[selectedIndexes.row],
						rows.dividers,
						...edges.rows,
					);
				}
				if (!_.isNil(selectedIndexes.col !== null)) {
					resizeRest(
						selectedIndexes.col,
						cols.dividers[selectedIndexes.col],
						cols.dividers,
						...edges.cols,
					);
				}
				isMouseDown = false;
				selectedIndexes.row = null;
				selectedIndexes.col = null;

				// reset cursor style to crosshair
				e.target.style.cursor = "crosshair";
			};
		};

		return Tool;
	})();

	/**
	 * Table header definition tool for tabular data.
	 * @param  {VueComponent} vm - DataEntry Vue instance
	 * @return {Function}          WPD Tool
	 */
	wpd.TableHeaderTool = (function (vm) {
		const Tool = function (calibration, readOnly) {
			const area = calibration.area;
			const rows = calibration.rows;
			const cols = calibration.cols;
			const rotation = calibration.rotation ?? 0;

			const allDividers = wpd.alignAxes.getFullDividers(
				area,
				rotation,
				rows.dividers,
				cols.dividers,
			);

			const allRowDividers = allDividers.rows;
			const allColDividers = allDividers.cols;

			const hover = {
				header: {
					rows: null,
					cols: null,
				},
			};

			let selectedRow = null;
			let selectedCol = null;

			const removeHeaderDataFromDataPoint = (dataPoint) => {
				const keys = _.chain(dataPoint.metadata)
					.omitBy(wpd.utils.isWord)
					.keys()
					.value();

				// delete all existing header data points within
				// each cell
				_.each(keys, (key) => {
					// existing data only had hidden flag, not
					// headerTag flag
					if (dataPoint.metadata[key].hidden) {
						delete dataPoint.metadata[key];
					}
				});

				// delete any composite keys on the data point
				delete dataPoint.metadata.compositeKey;
				delete dataPoint.metadata.referenceCompositeKey;
			};

			const addHeaderDataToDataPoint = (
				dataPoint,
				currentXHeader,
				currentYHeader,
				indexes,
			) => {
				const xHeaderValues = Object.values(
					_.omitBy(currentXHeader, wpd.utils.isWord),
				);
				const yHeaderValues = Object.values(
					_.omitBy(currentYHeader, wpd.utils.isWord),
				);
				const newHeaderValues = [...xHeaderValues, ...yHeaderValues];

				for (let i = 0; i < newHeaderValues.length; i++) {
					dataPoint.metadata[i + indexes.length] = {
						...newHeaderValues[i],
						hidden: true,
						headerTag: true,
					};
				}

				wpd.custom.filterDuplicateCellData(dataPoint.metadata);
				wpd.custom.updatePointTagGroups(dataPoint.metadata);

				const compositeKey =
					currentYHeader?.compositeKey ?? currentXHeader?.compositeKey;

				if (compositeKey) {
					dataPoint.metadata.compositeKey = compositeKey;
				}

				const referenceCompositeKey =
					currentYHeader?.referenceCompositeKey ??
					currentXHeader?.referenceCompositeKey;

				if (referenceCompositeKey) {
					dataPoint.metadata.referenceCompositeKey = referenceCompositeKey;
				}
			};

			const captureHeader = async (headerType) => {
				const index = headerType === "rows" ? selectedRow : selectedCol;
				if (index === null) {
					return;
				}

				let headerPosition;
				if (headerType === "rows") {
					const row = wpd.alignAxes.getRow(
						selectedRow,
						allRowDividers,
						area,
						null,
						rotation,
					);

					headerPosition = row.headerDialogPosition;
				} else {
					const col = wpd.alignAxes.getCol(
						selectedCol,
						allColDividers,
						area,
						null,
						rotation,
					);

					headerPosition = col.headerDialogPosition;
				}

				const { x: sx, y: sy } =
					wpd.alignAxes.getRotatedScreenCoordinates(headerPosition);

				// include offset and scroll positions from wpd graphics container
				const graphicsContainer =
					wpdDocument.getElementById("graphicsContainer");
				const canvasDiv = wpdDocument.getElementById("canvasDiv");
				const marginLeftText = wpdWindow.getComputedStyle(canvasDiv).marginLeft;
				const marginLeft = parseInt(marginLeftText.replace("px", ""), 10);
				const finalPosition = {
					x: sx - graphicsContainer.scrollLeft + marginLeft,
					y: sy - graphicsContainer.scrollTop,
				};

				// fetch existing axis metadata to modify
				const axis = wpd.tree.getActiveAxes();
				const axisMetadata = axis.getMetadata();

				const selectedTagGroup = axisMetadata.calibration?.cellTagGroup;
				const selectedTagGroups = axisMetadata.calibration?.cellTagGroups;

				// pre-fill new header with tags
				if (_.isEmpty(calibration[headerType].headers[index])) {
					// collect unique set of header tags
					const uniqueHeaderTags = new Map();
					calibration[headerType].headers.forEach((header) => {
						Object.values(_.omitBy(header, wpd.utils.isWord)).forEach(
							(data) => {
								if (data.tag?.uuid && !data.hidden) {
									const existingHeaderTag = uniqueHeaderTags.get(
										data.tag.uuid,
									) ?? { tagGroup: data.tagGroup };

									if (!!data.applyToAllHeaders) {
										// For branch (key) tags, carry the source header's full
										// composite key forward so the dialog round-trips a real
										// composite key. Copying just `data.value` (the parsed
										// branch name) would cause post-processing below to
										// reparse a bare string and mis-slot it.
										const isKey = wpd.tagGroups.isKeyTag(data.tag.name);
										existingHeaderTag.value =
											isKey && header.compositeKey
												? header.compositeKey
												: data.value;
									}
									uniqueHeaderTags.set(data.tag.uuid, existingHeaderTag);
								}
							},
						);
					});

					uniqueHeaderTags.keys().forEach((tagUUID, tagIndex) => {
						if (_.isNil(calibration[headerType].headers[index])) {
							calibration[headerType].headers[index] = {};
						}

						const headerTagMetadata = uniqueHeaderTags.get(tagUUID);
						calibration[headerType].headers[index][tagIndex] = {
							value: headerTagMetadata.value,
							disableApplyToAllHeaders: !!headerTagMetadata.value,
							tagUUID: tagUUID,
							tagGroup: headerTagMetadata.tagGroup,
						};
					});
				}

				// trigger metadata dialog
				this.isDialogOpen = true;

				const header = await vm
					.captureMetadata({
						extractionCompositeKeys: wpd.tagGroups.compositeKeys,
						tagGroupTabs: selectedTagGroups,
						axisType: "tableHeader",
						data: calibration[headerType].headers[index],
						forceDirection: headerType === "rows" ? "east" : "south",
						headerType: headerType === "rows" ? "row" : "column",
						position: finalPosition,
						limitTagGroups: selectedTagGroups ?? [selectedTagGroup],
						studyArmAbbreviations: wpd.tagGroups.getAllStudyArmKeys(),
						selectTextFunction: wpd.acquireData.selectText,
					})
					.then(async (metadata) => {
						if (metadata.location) {
							// not supported
							delete metadata.location;
						}

						this.isDialogOpen = false;
						return metadata;
					});

				// filter incomplete tags (missing either the tag or the value) from the set
				const filteredHeader = wpd.custom.filterCapturedMetadata(header);

				// update hidden data points on all corresponding datasets
				const axisDatasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);

				// handle composite key metadata
				const studyKeyTag = _.find(wpd.tagGroups.tags, {
					name: wpd.CompositeKeyUtils.studyTag,
					isCompositeKeyMember: true,
				});
				const partKeyTag = _.find(wpd.tagGroups.tags, {
					name: wpd.CompositeKeyUtils.studyPartTag,
					isCompositeKeyMember: true,
				});
				const armKeyTag = _.find(wpd.tagGroups.tags, {
					name: wpd.CompositeKeyUtils.studyArmTag,
					isCompositeKeyMember: true,
				});
				const populationKeyTag = _.find(wpd.tagGroups.tags, {
					name: wpd.CompositeKeyUtils.populationTag,
					isCompositeKeyMember: true,
				});

				let maxIndex = Math.max(
					...Object.keys(filteredHeader)
						.filter((key) => !isNaN(key))
						.map((key) => parseInt(key, 10)),
				);

				const extraData = [];
				let hasKey = false;
				let hasSourceSet = new Set();
				Object.entries(filteredHeader).forEach(([key, data]) => {
					if (wpd.utils.isWord(data, key)) {
						return;
					}

					// If apply to all headers is set, then unset the apply toggle for other headers
					const isSource = !!data.applyToAllHeaders;
					const tagUuid = data.tag?.uuid;

					// Find all entries with the same tag across other headers
					const matchingEntries = calibration[headerType].headers.flatMap(
						(otherHeader, otherIndex) => {
							if (!otherHeader || otherIndex === index) return [];

							return Object.entries(otherHeader)
								.filter(
									([key, otherData]) =>
										!wpd.utils.isWord(otherData, key) &&
										otherData.tag?.uuid === tagUuid,
								)
								.map(([, otherData]) => otherData);
						},
					);

					// Check if another header already claimed this tag as source
					const otherHeaderIsSource = matchingEntries.some(
						(entry) => entry.applyToAllHeaders,
					);

					// Update all matching entries
					matchingEntries.forEach((otherData) => {
						if (isSource) {
							hasSourceSet.add(tagUuid);
							otherData.applyToAllHeaders = false;
							otherData.disableApplyToAllHeaders = true;
						} else if (!hasSourceSet.has(tagUuid) && !otherHeaderIsSource) {
							otherData.disableApplyToAllHeaders = false;
						}
					});

					const isKey = wpd.tagGroups.isKeyTag(data.tag.name);
					if (isKey) {
						hasKey = true;
						filteredHeader.compositeKey = data.value;
						const parsedKey = wpd.CompositeKeyUtils.parseCompositeKey(
							data.value,
						);
						data.isCompositeKey = true;
						const tagGroup = data.tagGroup;
						switch (data.tag.name) {
							case wpd.CompositeKeyUtils.subgroupTag:
								data.value = parsedKey.subgroup;

								if (parsedKey.population) {
									extraData.push(
										_buildHeaderMetadataPoint(
											populationKeyTag,
											parsedKey.population,
											tagGroup,
										),
									);
								}

								extraData.push(
									_buildHeaderMetadataPoint(
										armKeyTag,
										parsedKey.studyArm,
										tagGroup,
									),
								);

								if (parsedKey.studyPart) {
									extraData.push(
										_buildHeaderMetadataPoint(
											partKeyTag,
											parsedKey.studyPart,
											tagGroup,
										),
									);
								}

								extraData.push(
									_buildHeaderMetadataPoint(
										studyKeyTag,
										parsedKey.study,
										tagGroup,
									),
								);

								break;
							case wpd.CompositeKeyUtils.populationTag:
								data.value = parsedKey.population;
								extraData.push(
									_buildHeaderMetadataPoint(
										armKeyTag,
										parsedKey.studyArm,
										tagGroup,
									),
								);

								if (parsedKey.studyPart) {
									extraData.push(
										_buildHeaderMetadataPoint(
											partKeyTag,
											parsedKey.studyPart,
											tagGroup,
										),
									);
								}

								extraData.push(
									_buildHeaderMetadataPoint(
										studyKeyTag,
										parsedKey.study,
										tagGroup,
									),
								);

								break;
							case wpd.CompositeKeyUtils.studyArmTag:
								data.value = parsedKey.studyArm;
								if (parsedKey.studyPart) {
									extraData.push(
										_buildHeaderMetadataPoint(
											partKeyTag,
											parsedKey.studyPart,
											tagGroup,
										),
									);
								}

								extraData.push(
									_buildHeaderMetadataPoint(
										studyKeyTag,
										parsedKey.study,
										tagGroup,
									),
								);

								break;
							case wpd.CompositeKeyUtils.studyPartTag:
								data.value = parsedKey.studyPart;
								extraData.push(
									_buildHeaderMetadataPoint(
										studyKeyTag,
										parsedKey.study,
										tagGroup,
									),
								);
								break;
							case wpd.CompositeKeyUtils.studyTag:
								data.value = parsedKey.study;
								break;
						}
					}
				});

				if (extraData.length > 0) {
					extraData.forEach((data) => {
						filteredHeader[++maxIndex] = data;
					});
				} else if (!hasKey) {
					// remove composite key if necessary
					delete filteredHeader.compositeKey;
				}

				// check if there is any remaining valid metadata
				const hasValidMetadata = Object.keys(filteredHeader).some(
					(key) => !wpd.utils.isWord(filteredHeader[key], key),
				);

				// set both metadata and local calibration to enable repainting
				if (!hasValidMetadata) {
					// set header to null if nothing was specified
					axisMetadata.calibration[headerType].headers[index] = null;
					calibration[headerType].headers[index] = null;
				} else {
					axisMetadata.calibration[headerType].headers[index] = filteredHeader;
					calibration[headerType].headers[index] = filteredHeader;
				}

				// write the metadata to the axis
				axis.setMetadata(axisMetadata);

				wpd.alignAxes.updateTagGroupCheckboxes(axisMetadata);
				wpd.alignAxes.updateHeaderTypeOptions(axisMetadata.calibration);

				const yHeaders = axisMetadata.calibration.cols.headers;
				const xHeaders = axisMetadata.calibration.rows.headers;

				// update all existing table cell data with newly set header
				// data
				switch (headerType) {
					case "cols":
						// get dataset associated with the axis and col index
						const dataset = _.find(axisDatasets, ["_metadata.colIndex", index]);
						const currentYHeader = yHeaders[index];

						_.each(dataset.getAllPixels(), (dataPoint) => {
							// each data point here belongs to a separate row
							// delete header data from each cell down the column
							removeHeaderDataFromDataPoint(dataPoint);

							// tag-value was added by via header tag, update all
							// header tags to current column header and re-add
							// all row headers
							const currentXHeader = xHeaders[dataPoint.metadata.rowIndex];
							const indexes = Object.keys(
								_.omitBy(dataPoint.metadata, wpd.utils.isWord),
							);

							addHeaderDataToDataPoint(
								dataPoint,
								currentXHeader,
								currentYHeader,
								indexes,
							);
						});

						break;
					case "rows":
						// each dataset associated with the axis is a column
						// loop through each column to access and update rows
						const currentXHeader = xHeaders[index];

						_.each(axisDatasets, (dataset) => {
							const datasetDataPoints = dataset.getAllPixels();

							if (
								_.findIndex(datasetDataPoints, ["metadata.rowIndex", index]) < 0
							) {
								// skip the column (dataset) if there isn't a
								// data point in the current row
								return;
							}

							const dataPoint = _.find(datasetDataPoints, [
								"metadata.rowIndex",
								index,
							]);

							// delete header data from each cell down the row
							removeHeaderDataFromDataPoint(dataPoint);

							// tag-value was added by via header tag, update all
							// header tags to current row header and re-add
							// all column headers
							const currentYHeader = yHeaders[dataset.getMetadata().colIndex];
							const indexes = Object.keys(
								_.omitBy(dataPoint.metadata, wpd.utils.isWord),
							);

							addHeaderDataToDataPoint(
								dataPoint,
								currentXHeader,
								currentYHeader,
								indexes,
							);
						});

						break;
				}

				// clear selected row and column
				selectedRow = null;
				selectedCol = null;

				await dataEntryVM.autoSaveMetadata();

				// trigger next empty header
				if (hasValidMetadata) {
					selectNextEmptyHeader(index, headerType);
				} else {
					// repaint if nothing was entered or existing data was deleted
					hover.header[headerType] = null;
					wpd.graphicsWidget.forceHandlerRepaint();
				}
			};

			const _buildHeaderMetadataPoint = (tag, value, tagGroup) => {
				return {
					tag: {
						name: tag.name,
						uuid: tag.uuid,
					},
					value: value,
					hidden: true,
					headerTag: true,
					...(tagGroup ? { tagGroup: tagGroup } : {}),
				};
			};

			const selectNextEmptyHeader = (index, headerType) => {
				// last header or index out of bounds, immediately repaint and exit
				if (index >= calibration[headerType].headers.length - 1) {
					hover.header[headerType] = null;
					wpd.graphicsWidget.forceHandlerRepaint();
					return;
				}

				for (let i = index; i < calibration[headerType].headers.length; i++) {
					if (calibration[headerType].headers[i] === null) {
						// set selected row or column
						if (headerType === "rows") {
							selectedRow = i;
						} else {
							selectedCol = i;
						}

						// set hover
						hover.header[headerType] = i;

						// repaint to update hover
						wpd.graphicsWidget.forceHandlerRepaint();

						// trigger header dialog
						setTimeout(function () {
							captureHeader(headerType);
						}, 200);

						return;
					}
				}
			};

			this.onAttach = function () {
				wpd.graphicsWidget.resetData();

				// set the repainter
				wpd.graphicsWidget.setRepainter(
					new wpd.TableRepainter({
						area: area,
						rows: rows,
						cols: cols,
						hover: hover,
						rotation: rotation,
						drawHeaders: !readOnly,
					}),
				);

				// paint the loaded data
				wpd.graphicsWidget.forceHandlerRepaint();
			};

			this.onMouseMove = function (e, pos, imagePos) {
				if (readOnly) {
					return;
				}
				const previousHoverRow = hover.header.rows;
				const previousHoverCol = hover.header.cols;

				// process row header hover
				if (!calibration.rows.headerless) {
					const hoveredRow = wpd.alignAxes.getHoveredRow(
						area,
						allRowDividers,
						imagePos,
						rotation,
					);
					const rowHeaderHit = wpd.alignAxes.rowHeaderTest(
						area,
						rows,
						cols,
						imagePos,
						rotation,
					);

					if (rowHeaderHit && hoveredRow) {
						hover.header.rows = hoveredRow.rowIndex;
						if (previousHoverRow !== hover.header.rows) {
							wpd.graphicsWidget.forceHandlerRepaint();
						}
					} else {
						if (previousHoverRow !== null) {
							hover.header.rows = null;
							wpd.graphicsWidget.forceHandlerRepaint();
						}
					}
				}

				// process column header hover
				if (!calibration.cols.headerless) {
					const hoveredCol = wpd.alignAxes.getHoveredCol(
						area,
						allColDividers,
						imagePos,
						rotation,
					);
					const colHeaderHit = wpd.alignAxes.colHeaderTest(
						area,
						rows,
						cols,
						imagePos,
						rotation,
					);
					if (colHeaderHit && hoveredCol) {
						hover.header.cols = hoveredCol.colIndex;
						if (previousHoverCol !== hover.header.cols) {
							wpd.graphicsWidget.forceHandlerRepaint();
						}
					} else {
						if (previousHoverCol !== null) {
							hover.header.cols = null;
							wpd.graphicsWidget.forceHandlerRepaint();
						}
					}
				}
			};

			this.onMouseClick = function (e, pos, imagePos) {
				if (readOnly) {
					return;
				}
				const rowHit = wpd.alignAxes.rowHeaderTest(
					area,
					rows,
					cols,
					imagePos,
					rotation,
				);
				const colHit = wpd.alignAxes.colHeaderTest(
					area,
					rows,
					cols,
					imagePos,
					rotation,
				);

				if (!calibration.rows.headerless && rowHit) {
					// check if mouse position is currently over row headers
					selectedRow = hover.header.rows;
					selectedCol = null;
					captureHeader("rows");
				} else if (!calibration.cols.headerless && colHit) {
					// check if mouse position is currently over column headers
					selectedCol = hover.header.cols;
					selectedRow = null;
					captureHeader("cols");
				}
			};
		};

		return Tool;
	})(dataEntryVM);

	/**
	 * Table cell editing tool for tabular data.
	 * @return {Function} WPD Tool
	 */
	wpd.TableCellSelectionTool = (function (vm) {
		const Tool = function (axis, activeCol) {
			const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);
			const dataset = datasets[activeCol];

			const calibration = axis.getMetadata().calibration;
			const area = calibration.area;
			const rows = calibration.rows;
			const cols = calibration.cols;
			const rotation = calibration.rotation ?? 0;
			const numRows = calibration.rows.num;
			const numCols = calibration.cols.num;
			const schema = calibration.schema;

			const allDividers = wpd.alignAxes.getFullDividers(
				area,
				rotation,
				rows.dividers,
				cols.dividers,
			);

			const allRowDividers = allDividers.rows;
			const allColDividers = allDividers.cols;

			let lock = false;
			let nextCell = false;
			let isAttached = false;

			const container = wpdDocument.getElementById("graphicsContainer");

			// locally store data from all data sets, organized by columns and rows
			const colData = [];

			// initialize empty cells set
			// empty cells are locally stored as sets, but written to dataset metadata as arrays (json)
			const emptyCells = new Set(dataset.getMetadata().emptyCells || []);

			// maps row indexes to dataset indexes
			const activeIndexMap = [];

			const hover = {
				cell: {
					rows: null,
					cols: null,
				},
			};

			const activeCell = {
				row: -1,
				col: activeCol,
			};

			// existing tags cache
			const existingTags = [];

			const getPixel = () => {
				let pixel;

				if (colData[activeCell.col][activeCell.row]) {
					// cell has existing data
					pixel = colData[activeCell.col][activeCell.row];
				} else {
					// get center of cell
					const { x, y } = wpd.alignAxes.getCell(
						activeCell.row,
						activeCell.col,
						allRowDividers,
						allColDividers,
						area,
						rotation,
					);

					// empty cell, create data point
					const pixelIndex = dataset.addPixel(x, y, {});
					pixel = dataset.getPixel(pixelIndex);

					// add to local data
					colData[activeCell.col][activeCell.row] = pixel;
					activeIndexMap[activeCell.row] = pixelIndex;
				}

				return pixel;
			};

			const hideNoHeaderHint = () => {
				vm.hideNoHeaderHint();
			};

			const showNoHeaderHint = (row, col) => {
				const { x, y } = wpd.alignAxes.getCell(
					row,
					col,
					allRowDividers,
					allColDividers,
					area,
					rotation,
				);
				// const cellPosition = getCellImagePosition();

				const screenPosition = wpd.alignAxes.getRotatedScreenCoordinates({
					x,
					y,
				});

				// include scroll positions from wpd graphics container
				const finalPosition = adjustForScroll(screenPosition);

				let mode = "";
				if (rows.headers[row] === null && cols.headers[col] === null) {
					mode = "both";
				} else if (rows.headers[row] === null) {
					mode = "row";
				} else if (cols.headers[col] === null) {
					mode = "col";
				}

				vm.showNoHeaderHint({
					mode: mode,
					position: finalPosition,
				});
			};

			const selectColumn = (index, skipMetadataDialog) => {
				// only allow selection if column header is defined
				if (
					calibration.cols.headers[index] ||
					(calibration.cols.headerless &&
						calibration.rows.headers[activeCell.row])
				) {
					hideNoHeaderHint();

					// set rowIndex on the selected dataset
					datasets[index].setMetadata(
						_.assign(datasets[index].getMetadata(), {
							selectRowIndex: activeCell.row,
							skipMetadataDialog: skipMetadataDialog,
						}),
					);

					// switch column, find all tree items and match on name
					wpd.tree.selectDataset(datasets[index].name);
				} else {
					showNoHeaderHint(activeCell.row, index);
				}
			};

			const selectNextCell = async () => {
				const rowIndex = activeCell.row;
				const colIndex = activeCell.col;

				// last row and last column; no next cell, return
				if (rowIndex >= numRows - 1 && colIndex >= numCols - 1) {
					wpd.custom.repaintDataPoints(wpd.tree.getActiveDataset());
					lock = false;
					return;
				}

				// last row in the column, go to the first row of the next column
				// otherwise, go to the next row of the current column
				if (rowIndex >= numRows - 1 && colIndex < numCols - 1) {
					activeCell.col++;
					activeCell.row = 0;
					selectColumn(activeCell.col, true);
				} else if (rowIndex < numRows - 1) {
					activeCell.row++;
				}

				// set hover
				hover.cell.rows = activeCell.row;
				hover.cell.cols = activeCell.col;

				// update zoomed image
				const { x, y } = wpd.alignAxes.getCell(
					activeCell.row,
					activeCell.col,
					allRowDividers,
					allColDividers,
					area,
					rotation,
				);
				// const imagePos = getCellImagePosition();
				wpd.graphicsWidget.updateZoomToImagePosn(x, y);

				// repaint to update hover
				wpd.custom.repaintDataPoints(wpd.tree.getActiveDataset());

				// otherwise, release lock and allow other interactions
				lock = false;
			};

			const collectTags = (metadata) => {
				const tags = _.chain(metadata)
					.omitBy(wpd.utils.isWord)
					.filter((data) => !data.hidden)
					.map((data) => {
						return {
							name: data.tag.name,
							uuid: data.tag.uuid,
							tagGroup: data.tagGroup,
						};
					})
					// filter out tags without meaningful data
					.filter((tag) => tag.name && tag.uuid)
					.value();

				// Key on tag uuid + tag-group uuid so that the same tag UUID
				// in two different tag-group tabs (e.g. "Individual value"
				// shared between Outcomes and Adverse Events) is partitioned
				// correctly. Without this, _.find below would return the first
				// matching tag in the tags array regardless of tag group, so
				// any extra entries would all carry the FIRST tag group's
				// stamp, leading to all rows landing in one tab and the
				// other appearing empty after carryover.
				const keyOf = (t) => `${t.uuid}::${t.tagGroup?.uuid ?? "_no_group"}`;

				if (existingTags.length === 0) {
					// empty existing tags, fill with tags
					existingTags.push(...tags);
				} else {
					if (!_.isEqual(existingTags, tags)) {
						// existingTags and tags are not deeply equal, find differences
						// cannot use _.difference since we want to detect duplicates as well
						const existingTagCounts = _.countBy(existingTags, keyOf);
						const tagCounts = _.countBy(tags, keyOf);

						// For each (tag, tag-group) pair seen in the new
						// cell that exceeds what's already in existingTags,
						// carry over one instance. Cap at one per visit on
						// purpose: pre-filling N empty rows for a tag the
						// previous cell had duplicated would be more noise
						// than help.
						_.keys(tagCounts).forEach((compositeKey) => {
							if (
								tagCounts[compositeKey] > (existingTagCounts[compositeKey] || 0)
							) {
								const representative = tags.find(
									(t) => keyOf(t) === compositeKey,
								);
								if (representative) {
									existingTags.push(representative);
								}
							}
						});
					}
					// do nothing if tags and existingTags are deeply equal
				}
			};

			const refreshTags = () => {
				// clear cached existing tags and values and recalculate
				existingTags.length = 0;

				// loop through every dataset (column)
				datasets.forEach((dataset, index) => {
					// loop through each existing data point (row)
					dataset.getAllPixels().forEach((pixel) => {
						// collect all existing tags
						switch (schema) {
							case "rows":
								if (pixel.metadata.rowIndex === activeCell.row) {
									collectTags(pixel.metadata);
								}
								break;
							case "cols":
								if (index === activeCell.col) {
									collectTags(pixel.metadata);
								}
								break;
							default:
								collectTags(pixel.metadata);
								break;
						}
					});
				});
			};

			const refreshData = () => {
				// fill in colData with empty arrays, each representing a column
				// NOTE cannot use Array.prototype.fill here, it does not create a new array for each slot
				colData.length = datasets.length;
				for (let i = 0; i < colData.length; i++) {
					colData[i] = [];
				}

				// each dataset is a column
				for (let i = 0; i < datasets.length; i++) {
					const pixels = datasets[i].getAllPixels();

					// for the active column, initialize empty dataset if necessary
					if (i === activeCell.col && pixels.length < 1) {
						datasets[i].setMetadataKeys(["rowIndex"]);
					}

					// reset local column data to nulls
					colData[i].length = numRows;
					colData[i].fill(null, 0);

					// check for cells marked as empty
					const datasetMetadata = datasets[i].getMetadata();
					if (datasetMetadata.emptyCells) {
						datasetMetadata.emptyCells.forEach(
							(j) => (colData[i][j] = undefined),
						);
					}

					// for the active column, reset local column data to dataset index map and set to nulls
					if (i === activeCell.col) {
						activeIndexMap.length = numRows;
						activeIndexMap.fill(null, 0);
					}

					// organize local column data in order of row indexes
					pixels.forEach((pixel, index) => {
						// fill in colData
						colData[i][pixel.metadata.rowIndex] = pixel;

						// for the active column, store dataset pixel index in a map
						if (i === activeCell.col) {
							activeIndexMap[pixel.metadata.rowIndex] = index;
						}
					});
				}
			};

			const addEmptyCell = (row) => {
				// add to current column
				emptyCells.add(row);

				// save to dataset metadata immediately
				saveEmptyCells();
			};

			const removeEmptyCell = (row) => {
				emptyCells.delete(row);

				// save to dataset metadata immediately
				saveEmptyCells();
			};

			const saveEmptyCells = () => {
				const metadata = dataset.getMetadata();

				if (emptyCells.size) {
					// convert set to array and overwrite existing metadata with local data
					metadata.emptyCells = Array.from(emptyCells);
				} else {
					// remove empty cells key from metadata if no local data
					delete metadata.emptyCells;
				}

				dataset.setMetadata(metadata);
			};

			const adjustForScroll = (position) => {
				const graphicsContainer =
					wpdDocument.getElementById("graphicsContainer");
				const canvasDiv = wpdDocument.getElementById("canvasDiv");
				const graphicsWidth = graphicsContainer.offsetWidth;
				const canvasWidth = canvasDiv.offsetWidth;
				const marginLeft =
					canvasWidth >= graphicsWidth ? 0 : (graphicsWidth - canvasWidth) / 2;

				return {
					x: position.x - container.scrollLeft + marginLeft,
					y: position.y - container.scrollTop,
				};
			};

			const scrollCellIntoView = (cell) => {
				const cellHeight = cell.screenHeight;
				const cellWidth = cell.screenWidth;

				const rotatedHeight = wpd.alignAxes.swapXY() ? cellWidth : cellHeight;
				const rotatedWidth = wpd.alignAxes.swapXY() ? cellHeight : cellWidth;

				const position = cell.rotatedCoords;
				const cellTop = position.y - rotatedHeight / 2;
				const cellRight = position.x + rotatedWidth / 2;
				const cellBottom = position.y + rotatedHeight / 2;
				const cellLeft = position.x - rotatedWidth / 2;

				// define viewport boundaries
				const top = container.scrollTop;
				const right = container.scrollLeft + container.clientWidth;
				const bottom = container.scrollTop + container.clientHeight;
				const left = container.scrollLeft;

				let x = container.scrollLeft;
				let y = container.scrollTop;

				// determine y scroll
				const paddedCellBottom = cellBottom + rotatedHeight;
				const paddedCellTop = cellTop - rotatedHeight;
				if (paddedCellBottom > bottom) {
					y = paddedCellBottom - container.clientHeight;
				} else if (paddedCellTop < top) {
					y = paddedCellTop;
				}

				// y scroll safeguards
				// if negative, set to 0; if greater than max, set to max
				const maxY = container.scrollHeight - container.clientHeight;
				y = Math.floor(y < 0 ? 0 : y > maxY ? maxY : y);

				// determine x scroll
				const paddedCellRight = cellRight + rotatedWidth;
				const paddedCellLeft = cellLeft - rotatedWidth;
				if (paddedCellRight > right) {
					x = paddedCellRight - container.clientWidth;
				} else if (paddedCellLeft < left) {
					x = paddedCellLeft;
				}

				// x scroll safeguards
				// if negative, set to 0; if greater than max, set to max
				const maxX = container.scrollWidth - container.clientWidth;
				x = Math.floor(x < 0 ? 0 : x > maxX ? maxX : x);

				return new Promise((resolve) => {
					const timeout = 2000;

					const guard = setTimeout(() => {
						container.removeEventListener("scroll", scrollHandler);
						dataEntryVM.clearNotification({ force: true });
						resolve({ status: "timeout" });
					}, timeout);

					const scrollHandler = _.debounce(() => {
						// scrollTop and scrollLeft may not be exact, check if within +/- 5 pixels
						if (
							Math.abs(x - Math.floor(container.scrollLeft)) < 5 &&
							Math.abs(y - Math.floor(container.scrollTop)) < 5
						) {
							// stop guard, remove listener, and resolve promise
							clearTimeout(guard);

							container.removeEventListener("scroll", scrollHandler);
							dataEntryVM.clearNotification({ force: true });
							resolve({ status: "ok" });
						}
					}, 100);

					// only scroll if necessary
					if (
						x !== Math.floor(container.scrollLeft) ||
						y !== Math.floor(container.scrollTop)
					) {
						// listen for scroll event and compare current scroll to target
						container.removeEventListener("scroll", scrollHandler);
						container.addEventListener("scroll", scrollHandler);

						dataEntryVM.showNotification({
							message: "Scrolling to cell...",
							status: "info",
							timeout,
						});

						// scroll
						container.scrollTo({
							top: y,
							left: x,
							behavior: "smooth",
						});
					} else {
						dataEntryVM.clearNotification({ force: true });
						resolve();
					}
				});
			};

			const getActiveCellHeaderData = (axesMetadata) => {
				const colHeaders = axesMetadata.calibration.cols.headers;
				const currentColHeader = colHeaders[activeCell.col];
				const rowHeaders = axesMetadata.calibration.rows.headers;
				const currentRowHeader = rowHeaders[activeCell.row];
				const colHeaderValues = Object.values(
					_.omitBy(currentColHeader, wpd.utils.isWord),
				);
				const rowHeaderValues = Object.values(
					_.omitBy(currentRowHeader, wpd.utils.isWord),
				);

				const cellTagGroups = _.uniqBy(
					[...rowHeaders, ...colHeaders]
						.filter((header) => !!header)
						.flatMap((header) => {
							return Object.keys(header)
								.filter((key) => {
									return !isNaN(key);
								})
								.map((key) => {
									return header[key].tagGroup;
								});
						})
						.filter((tagGroup) => !!tagGroup),
					"uuid",
				);

				return {
					rowHeaderData: rowHeaderValues,
					colHeaderData: colHeaderValues,
					compositeKey:
						currentColHeader?.compositeKey ?? currentRowHeader?.compositeKey,
					tagGroups: cellTagGroups,
				};
			};

			const addHeaderTagsToCell = (axesMetadata, metadata) => {
				const headerData = getActiveCellHeaderData(axesMetadata);
				const headerTags = [
					...headerData.rowHeaderData,
					...headerData.colHeaderData,
				];

				const indexes = Object.keys(_.omitBy(metadata, wpd.utils.isWord));
				const nextIndex = Math.max(...indexes.map(Number)) + 1;
				for (let i = 0; i < headerTags.length; i++) {
					metadata[i + nextIndex] = {
						...headerTags[i],
						hidden: true,
						headerTag: true,
					};
				}

				const compositeKey = headerData.compositeKey;

				if (compositeKey) {
					metadata.compositeKey = compositeKey;
				}
			};

			const findReference = (header) =>
				Object.values(_.omitBy(header, wpd.utils.isWord)).find(
					(data) => data.tag.name === wpd.CompositeKeyUtils.referenceBranchTag,
				);

			const captureData = (pixel) => {
				const cell = wpd.alignAxes.getCell(
					activeCell.row,
					activeCell.col,
					allRowDividers,
					allColDividers,
					area,
					rotation,
				);

				// scroll cell into view if necessary
				scrollCellIntoView(cell)
					.catch((error) => console.error(error))
					.finally(() => {
						// guard against tool removal during scroll
						if (!isAttached) {
							return;
						}

						// include scroll positions from wpd graphics container
						const finalPosition = adjustForScroll(cell.rotatedCoords);

						// strip rowIndex from existing data point before sending it to VMetadataDialog
						delete pixel.metadata.rowIndex;

						// this block seems completely unnecessary, tables do not have point groups
						// remove when this flag is removed
						if (!dataEntryVM.pointGroupOverhaul) {
							// add point groups as necessary
							if (dataset.hasPointGroups()) {
								const pointGroupUUIDs = dataset.getPointGroups();

								if (!_.isEmpty(pixel.metadata)) {
									// check if all point groups are tags
									for (const uuid of pointGroupUUIDs) {
										const ok = Object.values(
											_.omitBy(pixel.metadata, wpd.utils.isWord),
										).some((data) => data?.tag?.uuid === uuid);

										// if a point group is not included as a tag on this data point, add it
										if (!ok) {
											// get largest index
											const indexes = Object.keys(
												_.omitBy(pixel.metadata, wpd.utils.isWord),
											);

											pixel.metadata[indexes.length] = {
												value: null,
												tagUUID: uuid,
											};
										}
									}
								} else {
									// new data point, insert point groups as tags
									for (const uuid of pointGroupUUIDs) {
										// get largest index
										const indexes = Object.keys(
											_.omitBy(pixel.metadata, wpd.utils.isWord),
										);

										pixel.metadata[indexes.length] = {
											value: null,
											tagUUID: uuid,
										};
									}
								}
							}
						}

						// Compute the carryover suggestions for this cell. Done
						// regardless of whether the cell already has data: the
						// dialog applies them only to tabs that have no rows
						// of their own, so cells where one tab is filled and
						// another empty still get a useful pre-fill on the
						// empty tab.
						let defaultTags = undefined;
						refreshTags();
						if (existingTags.length > 0) {
							defaultTags = existingTags;
						}

						// load marked as blank
						const blankTableCell = emptyCells.has(activeCell.row);

						// setting a temp variable to row index to avoid out-of-
						// bounds indexes in other functions
						const rowIndex = activeCell.row;
						const axesMetadata = wpd.tree.getActiveAxes().getMetadata();

						const xReference = findReference(
							axesMetadata.calibration.rows.headers[activeCell.row],
						);
						const yReference = findReference(
							axesMetadata.calibration.cols.headers[activeCell.col],
						);

						// trigger metadata dialog
						this.isDialogOpen = true;

						const selectedTagGroup = axesMetadata.calibration?.cellTagGroup;
						const selectedTagGroups = axesMetadata.calibration?.cellTagGroups;

						const headerData = getActiveCellHeaderData(axesMetadata);

						vm.captureMetadata({
							dialogOffset: cell.dialogOffset,
							extractionCompositeKeys: wpd.tagGroups.compositeKeys,
							isBlankTableCell: blankTableCell,
							tagGroupTabs: selectedTagGroups,
							axisType: "table",
							defaultTags,
							data: pixel.metadata,
							headerData: headerData,
							excludeTags:
								xReference || yReference
									? [wpd.CompositeKeyUtils.referenceBranchTag]
									: [],
							forceAxis: "x",
							position: finalPosition,
							limitTagGroups: selectedTagGroups ?? [selectedTagGroup],
							studyArmAbbreviations: wpd.tagGroups.getAllStudyArmKeys(),
							selectTextFunction: wpd.acquireData.selectText,
						}).then(async (rawMetadata) => {
							if (rawMetadata.location) {
								// not supported
								delete rawMetadata.location;
							}
							// set rowIndex on metadata
							rawMetadata.rowIndex = rowIndex;
							rawMetadata.compositeKey = pixel.metadata.compositeKey;
							const metadata = wpd.custom.filterCapturedMetadata(rawMetadata);
							const notEmpty =
								!_.isEmpty(metadata) &&
								Object.keys(_.omitBy(metadata, wpd.utils.isWord)).filter(
									(key) =>
										!metadata[key].hidden &&
										!wpd.utils.isEmptyValue(metadata[key].value),
								).length;

							const hasExistingHeaderData = Object.keys(metadata)
								.filter((key) => !isNaN(key))
								.find((key) => metadata[key].headerTag);
							if (notEmpty) {
								if (!hasExistingHeaderData) {
									addHeaderTagsToCell(axesMetadata, metadata);
								}

								wpd.custom.filterDuplicateCellData(metadata);
								wpd.custom.updatePointTagGroups(metadata);

								Object.keys(metadata)
									.filter((key) => !isNaN(key))
									.forEach((key) => {
										if (
											metadata[key].value &&
											metadata[key].tag?.name ===
												wpd.CompositeKeyUtils.referenceBranchTag
										) {
											metadata.referenceCompositeKey = metadata[key].value;
										}
									});

								// update empty cell map
								// metadata.blank should never be true in this case
								if (!metadata.blank) {
									removeEmptyCell(rowIndex);
								}

								wpd.custom.saveDataPoint(
									dataset,
									activeIndexMap[rowIndex],
									metadata,
								);
							} else {
								// no metadata, delete data point
								dataset.removePixelAtIndex(activeIndexMap[rowIndex]);

								// update empty cell map
								if (metadata.blank) {
									addEmptyCell(rowIndex);
								} else {
									removeEmptyCell(rowIndex);
								}
							}

							// sync metadata keys
							wpd.custom.syncMetadataKeys(dataset);

							// refresh local data
							refreshData();

							// move to next cell if necessary
							if (nextCell) {
								selectNextCell();

								nextCell = false;
							} else {
								// repaint
								wpd.custom.repaintDataPoints(dataset);
							}

							await dataEntryVM.autoSaveMetadata();
							this.isDialogOpen = false;

							// release lock and allow other interactions
							lock = false;
						});
					});
			};

			const triggerActiveCell = () => {
				// trigger click handler with calculated cell position
				const { x, y } = wpd.alignAxes.getCell(
					activeCell.row,
					activeCell.col,
					allRowDividers,
					allColDividers,
					area,
					rotation,
				);

				// const imagePos = getCellImagePosition();

				this.onMouseMove(null, null, { x, y });
				this.onMouseClick(null, null, { x, y });
			};

			const hoverActiveCell = () => {
				hover.cell.rows = activeCell.row;
				hover.cell.cols = activeCell.col;
			};

			const nextCellHandler = () => {
				// event came from the vue app, the metadata
				// dialog was open, simply go to the next cell
				nextCell = true;
			};

			this.onAttach = function () {
				wpd.graphicsWidget.resetData();

				refreshData();

				// check for selected row index on active dataset
				const metadata = dataset.getMetadata();
				if (metadata.selectRowIndex !== undefined) {
					activeCell.row = metadata.selectRowIndex;

					hoverActiveCell();

					if (!metadata.skipMetadataDialog) {
						triggerActiveCell();
					}

					// delete selectRowIndex and set metadata
					delete metadata.selectRowIndex;
					delete metadata.skipMetadataDialog;
					dataset.setMetadata(metadata);
				}

				// set the repainter
				wpd.graphicsWidget.setRepainter(
					new wpd.TableRepainter({
						area: calibration.area,
						rows,
						cols,
						drawHeaders: false,
						activeCell,
						colData,
						hover,
						rotation,
					}),
				);

				// paint the loaded data
				wpd.graphicsWidget.forceHandlerRepaint();

				// handle next-cell event
				wpdDocument.removeEventListener("next-cell", nextCellHandler);
				wpdDocument.addEventListener("next-cell", nextCellHandler);

				isAttached = true;
			};

			this.onMouseMove = function (event, pos, imagePos) {
				if (lock) {
					return;
				}

				const previousHoverRow = hover.cell.rows;
				const previousHoverCol = hover.cell.cols;
				const isInTable = wpd.alignAxes.isInTableArea(imagePos, area);
				if (isInTable) {
					const row = wpd.alignAxes.getHoveredRow(
						area,
						allRowDividers,
						imagePos,
						rotation,
					);

					const col = wpd.alignAxes.getHoveredCol(
						area,
						allColDividers,
						imagePos,
						rotation,
					);

					hover.cell.rows = row.rowIndex;
					hover.cell.cols = col.colIndex;

					if (
						previousHoverRow !== hover.cell.rows ||
						previousHoverCol !== hover.cell.cols
					) {
						// repaint the table on hover change
						wpd.graphicsWidget.forceHandlerRepaint();
					}
				} else {
					if (previousHoverRow !== null || previousHoverCol !== null) {
						hover.cell.rows = null;
						hover.cell.cols = null;

						// repaint the table on hover change
						wpd.graphicsWidget.forceHandlerRepaint();
					}
				}
			};

			this.onMouseClick = function (event, pos, imagePos) {
				if (lock) {
					return;
				}

				const isInTable = wpd.alignAxes.isInTableArea(imagePos, area);
				if (isInTable) {
					const row = wpd.alignAxes.getHoveredRow(
						area,
						allRowDividers,
						imagePos,
						rotation,
					);

					const col = wpd.alignAxes.getHoveredCol(
						area,
						allColDividers,
						imagePos,
						rotation,
					);

					const colIndex = col.colIndex;
					activeCell.row = row.rowIndex;
					if (colIndex === activeCell.col) {
						if (
							calibration.rows.headers[activeCell.row] ||
							calibration.rows.headerless
						) {
							// engage lock, prevent other interactions
							lock = true;

							hideNoHeaderHint();

							// trigger metadata dialog
							captureData(getPixel());
						} else {
							showNoHeaderHint(activeCell.row, colIndex);
						}

						wpd.graphicsWidget.forceHandlerRepaint();
					} else {
						// engage lock, prevent other interactions
						lock = true;

						// clicked on another column, switch to it
						selectColumn(colIndex);
					}
				} else {
					hideNoHeaderHint();
				}
			};

			this.onKeyDown = function (event) {
				lock = true;

				// default to row 0 if no active cell, assume there is always an
				// active column
				if (activeCell.row < 0) {
					if (
						_.includes(
							["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"],
							event.key,
						)
					) {
						activeCell.row = 0;
						hoverActiveCell();
					}

					lock = false;
				} else {
					let moved = false;

					// handle keys
					switch (event.key) {
						case "ArrowUp":
							if (activeCell.row - 1 >= 0) {
								// move up by 1
								activeCell.row--;

								moved = true;
							}

							lock = false;

							break;
						case "ArrowDown":
							if (activeCell.row + 1 < numRows) {
								// move down by 1
								activeCell.row++;

								moved = true;
							}

							lock = false;

							break;
						case "ArrowLeft":
							if (activeCell.col - 1 >= 0) {
								// move left by 1
								activeCell.col--;

								moved = true;

								selectColumn(activeCell.col, true);
							} else {
								lock = false;
							}

							break;
						case "ArrowRight":
							if (activeCell.col + 1 < numCols) {
								// move right by 1
								activeCell.col++;

								moved = true;

								selectColumn(activeCell.col, true);
							} else {
								lock = false;
							}

							break;
						case "Enter":
							lock = false;

							if (!this.isDialogOpen) {
								triggerActiveCell();
							}

							break;
						case "Escape":
							lock = false;

							break;
						default:
							lock = false;
					}

					if (moved) {
						hoverActiveCell();
						const cell = wpd.alignAxes.getCell(
							activeCell.row,
							activeCell.col,
							allRowDividers,
							allColDividers,
							area,
							rotation,
						);

						scrollCellIntoView(cell);
					}
				}

				// redraw table
				wpd.graphicsWidget.forceHandlerRepaint();
			};

			this.onRemove = function () {
				isAttached = false;
			};
		};

		return Tool;
	})(dataEntryVM);
}

export { apply };
